import { css } from '@emotion/react';

const FONT = `'Neue Haas Grotesk Display Pro', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;

const COLOR = {
    doneBg:          '#22EC9A',
    doneInk:         '#1FB874',
    warnBg:          '#F59E0B',
    warnInk:         '#B45309',
    activeBorder:    '#34C6F1',
    activeInk:       '#0E6E8E',
    activeRing:      'rgba(52, 198, 241, 0.18)',
    rejBg:           '#DC2626',
    rejInk:          '#9B2C2C',
    grayBorder:      '#E2E8F0',
    grayInk:         '#94A3B8',
    connectorActive: '#22EC9A',
    // Overlay del ciclo de vida del seguro sobre el último círculo (Seguros).
    // No reemplaza al hito (latch sigue "Aprobado"); solo cambia el badge visual.
    upcomingBg:      '#E3EEFC',
    upcomingBorder:  '#34C6F1',
    upcomingInk:     '#1565C0',
    cancelledBg:     '#FBEEE0',
    cancelledBorder: '#D97433',
    cancelledInk:    '#7A3A0C',
    expiredBg:       '#EEF2F6',
    expiredBorder:   '#94A3B8',
    expiredInk:      '#475569',
};

export const scrollableStepperCSS = (width = 290) => css`
    font-family: ${FONT};
    width: ${width}px;
    max-width: 100%;
    position: relative;
    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 7%, #000 93%, transparent 100%);
    mask-image:         linear-gradient(to right, transparent 0, #000 7%, #000 93%, transparent 100%);

    .ss-viewport {
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        scrollbar-width: none;
        -ms-overflow-style: none;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
    }
    .ss-viewport::-webkit-scrollbar { display: none; }

    .ss-track {
        display: flex;
        padding: 2px 0;
    }

    .ss-step {
        width: 76px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        outline: none;
        background: transparent;
        border: none;
        padding: 0;
        font-family: inherit;
    }
    .ss-step:focus-visible .ss-dot {
        outline: 2px solid ${COLOR.activeBorder};
        outline-offset: 2px;
    }

    .ss-row {
        display: flex;
        align-items: center;
        width: 100%;
    }

    .ss-line {
        flex: 1;
        height: 2px;
        background: ${COLOR.grayBorder};
        transition: background 0.18s ease;
        border-radius: 999px;
    }
    .ss-line.transparent { background: transparent; }
    .ss-line.active      { background: ${COLOR.connectorActive}; }

    .ss-dot {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        border: 1.5px solid ${COLOR.grayBorder};
        background: #ffffff;
        color: ${COLOR.grayInk};
        flex-shrink: 0;
        display: grid;
        place-items: center;
        font-size: 11px;
        font-weight: 700;
        line-height: 1;
        transition: all 0.18s ease;
    }
    .ss-dot.done {
        background: ${COLOR.doneBg};
        border-color: ${COLOR.doneBg};
        color: #ffffff;
    }
    .ss-dot.warn {
        background: ${COLOR.warnBg};
        border-color: ${COLOR.warnBg};
        color: #ffffff;
    }
    .ss-dot.active {
        background: #ffffff;
        border-color: ${COLOR.activeBorder};
        color: ${COLOR.activeInk};
        box-shadow: 0 0 0 3px ${COLOR.activeRing};
    }
    .ss-dot.rej {
        background: ${COLOR.rejBg};
        border-color: ${COLOR.rejBg};
        color: #ffffff;
    }
    .ss-dot.na,
    .ss-dot.pending {
        background: #ffffff;
        border-color: ${COLOR.grayBorder};
        color: ${COLOR.grayInk};
    }
    .ss-dot.na { border-style: dashed; }

    /* Overlay del ciclo de vida del seguro — el paso ya está completado
       (hito aprobado) pero el Insurance vinculado no está en estado 'active'. */
    .ss-dot.upcoming {
        background: ${COLOR.upcomingBg};
        border-color: ${COLOR.upcomingBorder};
        color: ${COLOR.upcomingInk};
    }
    .ss-dot.cancelled {
        background: ${COLOR.cancelledBg};
        border-color: ${COLOR.cancelledBorder};
        color: ${COLOR.cancelledInk};
    }
    .ss-dot.expired {
        background: ${COLOR.expiredBg};
        border-color: ${COLOR.expiredBorder};
        color: ${COLOR.expiredInk};
    }

    .ss-glyph {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
    }
    .ss-glyph svg {
        display: block;
        width: 10px;
        height: 10px;
    }

    .ss-label {
        margin-top: 5px;
        font-size: 10.5px;
        letter-spacing: 0.02em;
        white-space: nowrap;
        text-align: center;
        font-weight: 500;
        color: ${COLOR.grayInk};
        max-width: 76px;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .ss-label.active    { color: ${COLOR.activeInk};    font-weight: 600; }
    .ss-label.rej       { color: ${COLOR.rejInk};       font-weight: 600; }
    .ss-label.done      { color: ${COLOR.doneInk};      font-weight: 500; }
    .ss-label.warn      { color: ${COLOR.warnInk};      font-weight: 500; }
    .ss-label.upcoming  { color: ${COLOR.upcomingInk};  font-weight: 600; }
    .ss-label.cancelled { color: ${COLOR.cancelledInk}; font-weight: 600; }
    .ss-label.expired   { color: ${COLOR.expiredInk};   font-weight: 500; }

    &.is-requote .ss-dot.done {
        background: #F97316;
        border-color: #F97316;
    }
    &.is-requote .ss-dot.active {
        border-color: #EA580C;
        color: #C2410C;
        box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.18);
    }
    &.is-requote .ss-line.active {
        background: #F97316;
    }
`;
